import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

export default function HrOpsDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/analytics/hrops").then(({ data }) => setD(data)).catch(() => {});
  }, []);

  const activeTotal = d ? Object.values(d.stage_counts).reduce((a, b) => a + b, 0) : null;
  const breached = d ? d.sla_queue.filter((r) => r.sla_status === "Breached").length : null;
  const maxFunnel = d ? Math.max(...d.funnel.map((f) => f.count), 1) : 1;

  const tiles = [
    { label: "Active in pipeline", value: activeTotal, color: "#0E2240", to: "/pipeline", testid: "tile-active-pipeline" },
    { label: "Interviews today", value: d?.interviews_today?.length, color: "#1E5AA8", to: "/interviews", testid: "tile-interviews-today" },
    { label: "Joiners this week", value: d?.joiners_this_week, color: "#1B7F3B", to: "/joining", testid: "tile-joiners-week" },
    { label: "SLA breached MRFs", value: breached, color: "#BB162B", to: "/requisitions", testid: "tile-sla-breached" },
  ];

  return (
    <div data-testid="hrops-dashboard">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">HR Operations Command</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">Welcome, {user?.full_name?.split(" ")[0]}</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} data-testid={t.testid}
            className="bg-white border border-gray-200 rounded-sm p-4 hover:border-[#0E2240] transition-colors duration-200 block">
            <div className="text-3xl font-semibold tabular-nums" style={{ color: t.color }}>{t.value ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-1">{t.label}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="hrops-funnel">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">Hiring funnel — conversion &amp; drop-off</h2>
          {(d?.funnel || []).map((f) => (
            <div key={f.stage} className="mb-2.5">
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="text-[#0E2240] font-medium">{f.stage}</span>
                <span className="text-[#6B7280] tabular-nums">
                  {f.count}{f.conversion != null && ` · ${pct(f.conversion)} conv`}{f.drop_off != null && f.drop_off > 0 && ` · ${pct(f.drop_off)} drop`}
                </span>
              </div>
              <div className="h-4 bg-gray-100 rounded-sm overflow-hidden">
                <div className="h-full bg-[#0E2240]" style={{ width: `${(f.count / maxFunnel) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="hrops-recruiter-load">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">Recruiter load</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={TH}>Recruiter</th><th className={TH}>Active</th><th className={TH}>Where they stand</th></tr></thead>
              <tbody>
                {(d?.recruiter_load || []).map((r) => (
                  <tr key={r.recruiter}>
                    <td className={`${TD} font-medium`}>{r.recruiter}</td>
                    <td className={`${TD} tabular-nums`}>{r.active}</td>
                    <td className={TD}>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.stages).map(([s, n]) => (
                          <span key={s} className="text-[10px] bg-[#F5F6F8] border border-gray-200 rounded-sm px-1.5 py-0.5 text-[#6B7280]">{s} · {n}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
                {d && d.recruiter_load.length === 0 && <tr><td colSpan={3} className="px-2 py-6 text-center text-xs text-[#6B7280]">No active applications</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="hrops-sla-queue">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">SLA escalation queue</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className={TH}>MRF</th><th className={TH}>Role · Branch</th><th className={TH}>Pending With</th><th className={TH}>Days</th><th className={TH}>SLA</th></tr></thead>
              <tbody>
                {(d?.sla_queue || []).map((r) => (
                  <tr key={r.req_id}>
                    <td className={TD}><Link to={`/requisitions/${r.req_id}`} className="text-[#1E5AA8] hover:underline">{r.req_no}</Link></td>
                    <td className={TD}>{r.desig_name} · {r.branch_name}</td>
                    <td className={TD}>{r.pending_with || "—"}</td>
                    <td className={`${TD} tabular-nums`}>{r.days_in_stage ?? "—"}</td>
                    <td className={TD}>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm text-white ${r.sla_status === "Breached" ? "bg-[#BB162B]" : "bg-[#D97706]"}`}>{r.sla_status}</span>
                    </td>
                  </tr>
                ))}
                {d && d.sla_queue.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-[#6B7280]">No SLA breaches — clean board</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5" data-testid="hrops-interviews-today">
          <h2 className="font-serif text-base text-[#0E2240] mb-3">Interviews today</h2>
          {(d?.interviews_today || []).map((iv) => (
            <div key={iv.interview_id} className="flex items-center justify-between border border-gray-200 rounded-sm px-3 py-2 mb-1.5">
              <div>
                <span className="text-xs font-medium text-[#0E2240]">{iv.candidate_name || "—"}</span>
                <span className="text-[11px] text-[#6B7280] ml-2">Round {iv.round_no} · {iv.mode}</span>
              </div>
              <div className="text-[11px] tabular-nums text-[#6B7280]">
                {new Date(iv.scheduled_on).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                <span className={`ml-2 text-[10px] font-semibold ${iv.feedback_done ? "text-[#1B7F3B]" : "text-[#D97706]"}`}>
                  {iv.feedback_done ? "Feedback in" : "Feedback pending"}
                </span>
              </div>
            </div>
          ))}
          {d && d.interviews_today.length === 0 && <div className="text-xs text-[#6B7280] py-4">No interviews scheduled today</div>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-8" data-testid="hrops-source-effectiveness">
        <h2 className="font-serif text-base text-[#0E2240] mb-3">Source effectiveness</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className={TH}>Source</th><th className={TH}>Hires</th><th className={TH}>Cost / Hire</th><th className={TH}>90-day Survival</th></tr></thead>
            <tbody>
              {(d?.source_effectiveness || []).map((r) => (
                <tr key={r.source}>
                  <td className={`${TD} font-medium`}>{r.source}</td>
                  <td className={`${TD} tabular-nums`}>{r.hires}</td>
                  <td className={`${TD} tabular-nums`}>{inr(r.cost_per_hire)}</td>
                  <td className={`${TD} tabular-nums ${r.survival_90d != null && r.survival_90d < 0.7 ? "text-[#BB162B] font-semibold" : "text-[#1B7F3B]"}`}>{pct(r.survival_90d)}</td>
                </tr>
              ))}
              {d && d.source_effectiveness.length === 0 && <tr><td colSpan={4} className="px-2 py-6 text-center text-xs text-[#6B7280]">No hires recorded yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
